import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Sparkles, BookOpen, Star, Clock, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";

interface Template {
  id: string;
  title: string;
  description: string;
  genre: string;
  ageGroup: string;
  difficulty: string;
  estimatedTime: string;
  popularity: number;
  tags: string[];
  coverImage?: string;
  isPremium: boolean;
}

const Templates = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("all");
  const [selectedAge, setSelectedAge] = useState("all");
  
  // Mock data - will be replaced with real data from database
  const [templates, setTemplates] = useState<Template[]>([
    {
      id: "1",
      title: "The Brave Little Explorer",
      description: "A young adventurer discovers a hidden world and learns about courage and friendship.",
      genre: "adventure",
      ageGroup: "3-5",
      difficulty: "easy",
      estimatedTime: "10 minutes",
      popularity: 95,
      tags: ["courage", "friendship", "discovery"],
      isPremium: false
    },
    {
      id: "2",
      title: "The Magical Garden",
      description: "A child finds a secret garden where plants can talk and magic is real.",
      genre: "fantasy",
      ageGroup: "3-5",
      difficulty: "easy",
      estimatedTime: "12 minutes",
      popularity: 88,
      tags: ["magic", "nature", "wonder"],
      isPremium: false
    },
    {
      id: "3",
      title: "The Friendly Dragon",
      description: "A misunderstood dragon learns to make friends and share with others.",
      genre: "friendship",
      ageGroup: "3-5",
      difficulty: "easy",
      estimatedTime: "8 minutes",
      popularity: 92,
      tags: ["friendship", "sharing", "acceptance"],
      isPremium: false
    },
    {
      id: "4",
      title: "The Time Traveling Cat",
      description: "A curious cat discovers a time machine and visits different historical periods.",
      genre: "educational",
      ageGroup: "6-8",
      difficulty: "medium",
      estimatedTime: "15 minutes",
      popularity: 78,
      tags: ["history", "learning", "adventure"],
      isPremium: true
    },
    {
      id: "5",
      title: "The Rainbow Bridge",
      description: "A magical bridge appears to help forest friends cross a dangerous river.",
      genre: "fantasy",
      ageGroup: "6-8",
      difficulty: "medium",
      estimatedTime: "12 minutes",
      popularity: 85,
      tags: ["magic", "helping", "teamwork"],
      isPremium: false
    },
    {
      id: "6",
      title: "The Sleepy Moon",
      description: "A gentle bedtime story about the moon helping children fall asleep.",
      genre: "bedtime",
      ageGroup: "1-2",
      difficulty: "easy",
      estimatedTime: "5 minutes",
      popularity: 96,
      tags: ["bedtime", "calm", "sleep"],
      isPremium: false
    }
  ]);

  const genres = [
    { value: "all", label: "All Genres" },
    { value: "adventure", label: "Adventure", emoji: "🗺️" },
    { value: "fantasy", label: "Fantasy", emoji: "🐉" },
    { value: "educational", label: "Educational", emoji: "📚" },
    { value: "bedtime", label: "Bedtime", emoji: "🌙" },
    { value: "friendship", label: "Friendship", emoji: "🤝" },
    { value: "nature", label: "Nature", emoji: "🌿" }
  ];

  const ageGroups = [
    { value: "all", label: "All Ages" },
    { value: "1-2", label: "Toddlers (1-2 years)" },
    { value: "3-5", label: "Preschool (3-5 years)" },
    { value: "6-8", label: "Early Readers (6-8 years)" },
    { value: "9-12", label: "Middle Grade (9-12 years)" }
  ];

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesGenre = selectedGenre === "all" || template.genre === selectedGenre;
    const matchesAge = selectedAge === "all" || template.ageGroup === selectedAge;
    
    return matchesSearch && matchesGenre && matchesAge;
  });

  const getGenreEmoji = (genre: string) => {
    return genres.find(g => g.value === genre)?.emoji || "📖";
  };

  const getGenreColor = (genre: string) => {
    const colors: { [key: string]: string } = {
      adventure: "bg-blue-100 text-blue-800",
      fantasy: "bg-purple-100 text-purple-800",
      educational: "bg-green-100 text-green-800",
      bedtime: "bg-indigo-100 text-indigo-800",
      friendship: "bg-pink-100 text-pink-800",
      nature: "bg-emerald-100 text-emerald-800"
    };
    return colors[genre] || "bg-gray-100 text-gray-800";
  };

  const getDifficultyColor = (difficulty: string) => {
    const colors: { [key: string]: string } = {
      easy: "bg-green-100 text-green-800",
      medium: "bg-yellow-100 text-yellow-800",
      hard: "bg-red-100 text-red-800"
    };
    return colors[difficulty] || "bg-gray-100 text-gray-800";
  };

  const handleUseTemplate = (template: Template) => {
    navigate("/", {
      state: {
        template: template
      }
    });
  };

  // If still loading, show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <BookOpen className="h-8 w-8 animate-pulse text-purple-500" />
          <span className="text-lg">Loading templates...</span>
        </div>
      </div>
    );
  }

  // If not logged in, redirect
  if (!user) {
    navigate("/auth");
    return null;
  }

  return (
    <Layout>
      <div className="py-6 px-6">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="h-8 w-8 text-purple-500" />
            <h1 className="text-3xl font-bold text-gray-800">Story Templates</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose from our collection of beautiful story templates to get started quickly
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search templates..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select value={selectedGenre} onValueChange={setSelectedGenre}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {genres.map((genre) => (
                <SelectItem key={genre.value} value={genre.value}>
                  <span className="flex items-center space-x-2">
                    {genre.emoji && <span>{genre.emoji}</span>}
                    <span>{genre.label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedAge} onValueChange={setSelectedAge}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ageGroups.map((age) => (
                <SelectItem key={age.value} value={age.value}>
                  {age.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery || selectedGenre !== "all" || selectedAge !== "all" 
                ? "No templates found" 
                : "No templates available"
              }
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery || selectedGenre !== "all" || selectedAge !== "all"
                ? "Try adjusting your filters or search terms" 
                : "Check back later for new templates"
              }
            </p>
            {(searchQuery || selectedGenre !== "all" || selectedAge !== "all") && (
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery("");
                  setSelectedGenre("all");
                  setSelectedAge("all");
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <Card key={template.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getGenreEmoji(template.genre)}</span>
                      <Badge variant="secondary" className={getGenreColor(template.genre)}>
                        {template.genre}
                      </Badge>
                    </div>
                    {template.isPremium && (
                      <Badge variant="outline" className="text-yellow-600 border-yellow-200">
                        <Star className="h-3 w-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg">{template.title}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {template.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>{template.estimatedTime}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4" />
                        <span>{template.popularity}%</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span>Age:</span>
                      <span>{template.ageGroup} years</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span>Difficulty:</span>
                      <Badge variant="outline" size="sm" className={getDifficultyColor(template.difficulty)}>
                        {template.difficulty}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mt-3">
                    {template.tags.map((tag) => (
                      <Badge key={tag} variant="outline" size="sm" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  <Button 
                    className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                    onClick={() => handleUseTemplate(template)}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Use This Template
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Featured Section */}
        <div className="mt-12">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Create Your Own</h2>
            <p className="text-gray-600">Start from scratch and let your imagination run wild</p>
          </div>
          <div className="text-center">
            <Button 
              size="lg" 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={() => navigate("/")}
            >
              <Sparkles className="h-5 w-5 mr-2" />
              Start from Scratch
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Templates; 